import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_LAYER_GAINS, type LayerGains } from "./audio/engine";
import { DEFAULT_FEATURE_LAYER_GAINS, type FeatureLayerGains } from "./audio/featureEngine";
import {
  Sonifier,
  type ClassicScene,
  type FeatureScene,
  type SonificationScene,
} from "./audio/sonifier";
import {
  analyzeSpectrum,
  continuumLevel5100,
  type ExtractionSettings,
  type FeatureAnalysis,
} from "./audio/features";
import {
  readStoredExtractionSettings,
  writeStoredExtractionSettings,
} from "./audio/extractionStorage";
import { DEFAULT_BRIGHT_FLUX, epochToAudio, type BrightFlux } from "./audio/mapping";
import { AppHeader } from "./components/AppHeader";
import { EpochInfo } from "./components/EpochInfo";
import { EpochMixer } from "./components/EpochMixer";
import { AudioSection } from "./components/mixer/AudioSection";
import { CompareSection } from "./components/mixer/CompareSection";
import { DustSection } from "./components/mixer/DustSection";
import { EngineSection } from "./components/mixer/EngineSection";
import { ExtractionSection } from "./components/mixer/ExtractionSection";
import { SoloSection } from "./components/mixer/SoloSection";
import { StackedNote } from "./components/mixer/StackedNote";
import { SweepSection } from "./components/mixer/SweepSection";
import { NarrativePanel } from "./components/NarrativePanel";
import { PlotLegend } from "./components/PlotLegend";
import { PlotToolbar } from "./components/PlotToolbar";
import { SpectrumPlot, type PlotScene } from "./components/SpectrumPlot";
import { Timeline } from "./components/Timeline";
import { DEFAULT_OBJECT_ID, loadPackage, spectrumAt, uncertaintyAt } from "./data/loader";
import {
  applyExtinction,
  findEpochByState,
  interpolateAt,
  snapToNearestEpoch,
  type InterpolationResult,
} from "./data/spectra";
import type { ExtinctionLawName, SpectraPackage, SpectralState } from "./data/types";
import { epochLabel, hbetaOffsetKms, stateColor } from "./data/types";
import type { LineSolo } from "./domain/lines";
import type { Mode, SonificationMode } from "./domain/mode";
import { mjdToYear } from "./domain/physics";
import { readCanvasPalette } from "./theme/canvasPalette";
import {
  applyUiThemeToDocument,
  readStoredTheme,
  writeStoredTheme,
  type UiThemeId,
} from "./theme/uiTheme";

/** Calendar year of an MJD, rounded for axis labels. */
function yearLabel(mjd: number): number {
  return Math.round(mjdToYear(mjd));
}

export function App() {
  const [objectId, setObjectId] = useState<string>(DEFAULT_OBJECT_ID);
  const [pkg, setPkg] = useState<SpectraPackage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [uiTheme, setUiTheme] = useState<UiThemeId>(() => readStoredTheme());
  const [mode, setMode] = useState<Mode>("observed");
  const [comparisonIndex, setComparisonIndex] = useState<number | null>(null);
  const [cursorMjd, setCursorMjd] = useState(52057);
  const [lineSolo, setLineSolo] = useState<LineSolo>("all");
  const [showDifference, setShowDifference] = useState(false);

  const [reddeningAv, setReddeningAv] = useState(0.89);
  const [reddeningLaw, setReddeningLaw] = useState<ExtinctionLawName>("maiolino_agn");
  const [reddeningTemplateState, setReddeningTemplateState] =
    useState<SpectralState>("Intermediate");

  const [plotMaximised, setPlotMaximised] = useState(false);
  const [sonifier, setSonifier] = useState<Sonifier | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [masterGain, setMasterGain] = useState(0.55);
  const [layerGains, setLayerGains] = useState<LayerGains>(DEFAULT_LAYER_GAINS);
  const [sonificationMode, setSonificationMode] = useState<SonificationMode>("feature");
  const [featureLayerGains, setFeatureLayerGains] = useState<FeatureLayerGains>(
    DEFAULT_FEATURE_LAYER_GAINS,
  );

  // Stacked-mode state: one fader per epoch (manifest order) and an optional
  // solo index that masks all other branches when set.
  const [stackedEpochGains, setStackedEpochGains] = useState<number[]>([]);
  const [stackedSoloIndex, setStackedSoloIndex] = useState<number | null>(null);

  const [sweepLoop, setSweepLoop] = useState(false);
  /** Sweep tempo: 1 = default, 0.5–2 = half to double speed; retimes an in-flight sweep live. */
  const [sweepRate, setSweepRate] = useState(1);
  /** Wavelength of the sweep pluck sounding now, for the plot to highlight. */
  const [sweepLambdaA, setSweepLambdaA] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    loadPackage(objectId)
      .then((loaded) => {
        if (!cancelled) {
          setPkg(loaded);
          setCursorMjd(loaded.manifest.epochs[0].mjd);
          // Dust mode needs a non-dim state to redden; an object without one
          // (J0950) cannot stay in it.
          if (!loaded.manifest.epochs.some((e) => e.state && e.state !== "Dim")) {
            setMode((prev) => (prev === "reddening" ? "observed" : prev));
          }
          setStackedEpochGains(new Array(loaded.manifest.epochs.length).fill(1));
          // Epoch-indexed selections from the previous object are meaningless
          // for the new one; clear them rather than pointing at a stale row.
          setComparisonIndex(null);
          setStackedSoloIndex(null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [objectId]);

  const epochs = useMemo(() => pkg?.manifest.epochs ?? [], [pkg]);

  const brightFlux = useMemo<BrightFlux>(
    () => pkg?.manifest.audio_normalization ?? DEFAULT_BRIGHT_FLUX,
    [pkg],
  );

  // The displayed spectrum and its primary epoch both follow the timeline
  // cursor: a blend of the two bracketing epochs in interpolated mode, the
  // nearest epoch otherwise.
  const interpolation = useMemo<InterpolationResult | null>(() => {
    if (!pkg) return null;
    if (mode === "interpolated") {
      return interpolateAt(epochs, pkg, cursorMjd);
    }
    const index = snapToNearestEpoch(epochs, cursorMjd);
    return {
      primaryIndex: index,
      comparisonForBlendIndex: null,
      blend: 0,
      flux: spectrumAt(pkg, index).slice(),
    };
  }, [pkg, mode, cursorMjd, epochs]);

  const primaryIndex = interpolation?.primaryIndex ?? 0;
  const primary = epochs[primaryIndex];
  // A/B comparison has no meaning when every epoch is already on screen.
  const comparison =
    comparisonIndex !== null && mode !== "all_epochs" ? epochs[comparisonIndex] : null;
  const hasStates = epochs.some((e) => e.state !== null);

  // Dust-mode template choices: the states this object actually has, each
  // labelled with the year it was first observed in that state. Dim is excluded
  // because reddening an already-dim spectrum is not the comparison being made.
  const templateOptions = useMemo(() => {
    const seen = new Map<SpectralState, number>();
    for (const epoch of [...epochs].sort((a, b) => a.mjd - b.mjd)) {
      if (epoch.state && epoch.state !== "Dim" && !seen.has(epoch.state)) {
        seen.set(epoch.state, yearLabel(epoch.mjd));
      }
    }
    return [...seen.entries()].map(([state, year]) => ({
      state,
      label: `${state} (${year})`,
    }));
  }, [epochs]);

  // The chosen template if this object has it, else the first state it does
  // have (J0159 is Bright/Dim only), else null: dust mode is then unavailable.
  const templateState = useMemo<SpectralState | null>(() => {
    if (templateOptions.some((o) => o.state === reddeningTemplateState)) {
      return reddeningTemplateState;
    }
    return templateOptions[0]?.state ?? null;
  }, [templateOptions, reddeningTemplateState]);
  const templateEpoch = useMemo(
    () => (templateState ? findEpochByState(epochs, templateState) : null),
    [epochs, templateState],
  );

  const reddenedFlux = useMemo<Float32Array | null>(() => {
    if (!pkg || mode !== "reddening" || !templateEpoch) return null;
    const flux = spectrumAt(pkg, templateEpoch.index);
    return applyExtinction(flux, pkg.extinction[reddeningLaw], reddeningAv);
  }, [pkg, mode, templateEpoch, reddeningLaw, reddeningAv]);

  const comparisonFlux = useMemo<Float32Array | null>(() => {
    if (!pkg || comparison === null) return null;
    return spectrumAt(pkg, comparison.index).slice();
  }, [pkg, comparison]);

  // The sweep-only solo has no meaning outside feature mode, so leaving it
  // (via either control) drops back to "all".
  const clearSweepSolo = () => setLineSolo((prev) => (prev === "sweep" ? "all" : prev));

  const handleModeChange = useCallback((next: Mode) => {
    setMode(next);
    if (next === "observed" || next === "all_epochs") {
      setShowDifference(false);
    }
    if (next === "all_epochs") clearSweepSolo();
  }, []);

  const handleSonificationModeChange = useCallback((next: SonificationMode) => {
    setSonificationMode(next);
    if (next === "classic") clearSweepSolo();
  }, []);

  useEffect(() => {
    writeStoredTheme(uiTheme);
  }, [uiTheme]);

  // Applied synchronously so the canvas palette read on the next render sees the new theme.
  const handleUiThemeChange = useCallback((next: UiThemeId) => {
    applyUiThemeToDocument(next);
    setUiTheme(next);
  }, []);

  const handleObjectChange = useCallback((next: string) => {
    // Drop the current package immediately so no render can index the old
    // epoch arrays with indices that may not exist in the incoming object.
    setPkg(null);
    setObjectId(next);
  }, []);

  const handleStackedEpochGainChange = useCallback((index: number, value: number) => {
    setStackedEpochGains((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = prev.slice();
      next[index] = Math.max(0, Math.min(2, value));
      return next;
    });
  }, []);

  const handleStackedSoloToggle = useCallback((index: number) => {
    setStackedSoloIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleStackedClearSolo = useCallback(() => {
    setStackedSoloIndex(null);
  }, []);

  const handleStackedReset = useCallback(() => {
    setStackedEpochGains((prev) => prev.map(() => 1));
    setStackedSoloIndex(null);
  }, []);

  const handleSelectPrimary = useCallback(
    (index: number) => {
      const ep = epochs[index];
      if (ep) setCursorMjd(ep.mjd);
    },
    [epochs],
  );

  const handleToggleAudio = useCallback(() => {
    if (!sonifier) {
      Sonifier.create()
        .then((created) => {
          setSonifier(created);
          setAudioEnabled(true);
        })
        .catch((err: Error) => setError(`Audio failed to start: ${err.message}`));
      return;
    }
    sonifier.resume();
    setAudioEnabled((prev) => !prev);
  }, [sonifier]);

  // The sweep solo mutes every sustained voice so only the plucks remain, and
  // those plucks cover the full feature set; a line solo narrows the sweep too.
  const sweepSolo: LineSolo = lineSolo === "sweep" ? "all" : lineSolo;

  // Per-epoch trim values for stacked mode. Headroom is applied to the full
  // mix to keep the sum from clipping; when an epoch is solo'd the trim
  // bypasses the headroom so the remaining voice is comparable to single
  // mode.
  const stackedTrims = useMemo<number[]>(() => {
    const n = stackedEpochGains.length;
    if (n === 0) return [];
    const headroom = stackedSoloIndex === null ? 1 / Math.sqrt(n) : 1;
    return stackedEpochGains.map((g, i) => {
      if (stackedSoloIndex !== null && stackedSoloIndex !== i) return 0;
      return headroom * Math.max(0, g);
    });
  }, [stackedEpochGains, stackedSoloIndex]);

  // Feature-driven sonification is active in single-spectrum modes only;
  // all-epochs keeps the classic stacked engine.
  const featureModeActive = sonificationMode === "feature" && mode !== "all_epochs";

  // Live extraction thresholds, persisted like the UI theme.
  const [extractionSettings, setExtractionSettings] = useState<ExtractionSettings>(() =>
    readStoredExtractionSettings(),
  );
  useEffect(() => {
    writeStoredExtractionSettings(extractionSettings);
  }, [extractionSettings]);

  // The spectrum the feature sonifier listens to is the one on screen: the
  // reddened template in dust mode, otherwise the observed/interpolated flux.
  const sonifiedFlux = useMemo<Float32Array | null>(() => {
    if (!featureModeActive) return null;
    if (mode === "reddening") return reddenedFlux;
    return interpolation?.flux ?? null;
  }, [featureModeActive, mode, reddenedFlux, interpolation]);

  const featureAnalysis = useMemo<FeatureAnalysis | null>(() => {
    if (!pkg || !sonifiedFlux) return null;
    const uncertaintyEpoch =
      mode === "reddening" && templateEpoch ? templateEpoch.index : primaryIndex;
    return analyzeSpectrum(
      pkg.wavelength,
      sonifiedFlux,
      uncertaintyAt(pkg, uncertaintyEpoch),
      pkg.manifest.wavelength_rest.step_A,
      pkg.manifest.features,
      extractionSettings,
    );
  }, [pkg, sonifiedFlux, mode, templateEpoch, primaryIndex, extractionSettings]);

  // Continuum-pad normalisation: the object's brightest 5100 Å level, so pad
  // loudness is relative to this object's own bright state.
  const continuumScale = useMemo<number>(() => {
    if (!pkg) return 1;
    let max = 0;
    for (const epoch of pkg.manifest.epochs) {
      const level = continuumLevel5100(pkg.wavelength, spectrumAt(pkg, epoch.index));
      if (Number.isFinite(level) && level > max) max = level;
    }
    return max > 0 ? 1 / max : 1;
  }, [pkg]);

  // What the classic engine should voice: every epoch stacked, or the primary
  // (blended toward its interpolation partner, reddened in dust mode).
  const classicScene = useMemo<ClassicScene | null>(() => {
    if (!primary || !interpolation) return null;
    if (mode === "all_epochs") {
      return {
        kind: "stacked",
        layerGains,
        items: epochs.map((epoch, i) => ({
          parameters: epochToAudio(epoch, null, 0, 0, layerGains, lineSolo, brightFlux),
          trimLinear: stackedTrims[i] ?? 0,
        })),
      };
    }
    const partner =
      mode === "interpolated" && interpolation.comparisonForBlendIndex !== null
        ? epochs[interpolation.comparisonForBlendIndex]
        : null;
    const blend = mode === "interpolated" ? interpolation.blend : 0;
    const reddening = mode === "reddening" ? reddeningAv : 0;
    return {
      kind: "single",
      params: epochToAudio(primary, partner, blend, reddening, layerGains, lineSolo, brightFlux),
    };
  }, [primary, interpolation, mode, epochs, layerGains, lineSolo, brightFlux, stackedTrims, reddeningAv]);

  // Auto-sweep announces each new epoch in observed mode only; scrubbing in
  // interpolated mode stays sweep-free.
  const featureScene = useMemo<FeatureScene | null>(() => {
    if (!featureModeActive || !featureAnalysis) return null;
    return {
      analysis: featureAnalysis,
      referenceFlux: brightFlux.Hbeta,
      continuumScale,
      layerGains: featureLayerGains,
      sweepEpoch: mode === "observed" ? primaryIndex : null,
      sweepLoop,
      sweepRate,
      sweepSolo,
    };
  }, [featureModeActive, featureAnalysis, brightFlux, continuumScale, featureLayerGains, mode, primaryIndex, sweepLoop, sweepRate, sweepSolo]);

  const scene = useMemo<SonificationScene>(
    () => ({ enabled: audioEnabled, masterGain, solo: lineSolo, classic: classicScene, feature: featureScene }),
    [audioEnabled, masterGain, lineSolo, classicScene, featureScene],
  );

  useEffect(() => {
    sonifier?.apply(scene);
  }, [sonifier, scene]);

  useEffect(() => sonifier?.onSweepLambda(setSweepLambdaA), [sonifier]);

  const handlePlaySweep = useCallback(() => sonifier?.playSweep(), [sonifier]);

  // What the plot draws: every epoch stacked (the soloed one emphasised, the
  // rest dimmed so eye and ear agree), or the primary with its overlays.
  const plotScene = useMemo<PlotScene | null>(() => {
    if (!pkg || !primary || !interpolation) return null;
    if (mode === "all_epochs") {
      return {
        kind: "stacked",
        traces: epochs.map((epoch, i) => {
          const dim = stackedSoloIndex !== null && stackedSoloIndex !== i;
          return {
            flux: spectrumAt(pkg, epoch.index),
            color: stateColor(epoch.state),
            label: epochLabel(epoch),
            alpha: dim ? 0.18 : stackedSoloIndex === i ? 1.0 : 0.85,
            lineWidth: stackedSoloIndex === i ? 2.0 : 1.1,
          };
        }),
      };
    }
    return {
      kind: "single",
      primary: { flux: interpolation.flux, state: primary.state, hbetaOffsetKms: hbetaOffsetKms(primary) },
      comparison:
        comparison && comparisonFlux
          ? { flux: comparisonFlux, state: comparison.state, hbetaOffsetKms: hbetaOffsetKms(comparison) }
          : null,
      reddenedFlux,
      showDifference: showDifference && comparisonFlux !== null,
      featureMarkers:
        featureScene?.analysis.features.map((f) => ({ lambdaA: f.lambdaA, known: f.knownKey !== null })) ?? null,
      continuumFlux: featureScene?.analysis.continuum ?? null,
      sweepLambdaA,
    };
  }, [pkg, primary, interpolation, mode, epochs, stackedSoloIndex, comparison, comparisonFlux, reddenedFlux, showDifference, featureScene, sweepLambdaA]);

  // Canvases cannot inherit CSS colours, so the palette is read once per theme.
  const palette = useMemo(() => readCanvasPalette(), [uiTheme]);

  if (error) {
    return <div className="error">Failed to load: {error}</div>;
  }
  if (!pkg || !primary || !interpolation || !plotScene) {
    return <div className="loading">Loading spectra…</div>;
  }

  const object = pkg.manifest.object;
  const stacked = mode === "all_epochs";

  return (
    <div className={`app-shell${plotMaximised ? " plot-maximised" : ""}`}>
      <AppHeader
        object={object}
        mode={mode}
        onModeChange={handleModeChange}
        reddeningAvailable={templateOptions.length > 0}
        objectId={objectId}
        onObjectChange={handleObjectChange}
        uiTheme={uiTheme}
        onUiThemeChange={handleUiThemeChange}
      />

      <EpochInfo
        object={object}
        epochs={epochs}
        primaryIndex={primaryIndex}
        comparisonIndex={comparisonIndex}
        onSelectPrimary={handleSelectPrimary}
        onSelectComparison={setComparisonIndex}
        mode={mode}
        cursorMjd={cursorMjd}
        showInterpolated={interpolation.comparisonForBlendIndex !== null}
      />

      <div className="plot-area">
        <PlotToolbar
          mode={mode}
          epochCount={epochs.length}
          soloedDate={stackedSoloIndex !== null ? epochs[stackedSoloIndex]?.date ?? null : null}
          primary={primary}
          comparison={comparison}
          reddened={
            mode === "reddening" && templateState
              ? { state: templateState, av: reddeningAv, law: reddeningLaw }
              : null
          }
          maximised={plotMaximised}
          onToggleMaximised={() => setPlotMaximised((v) => !v)}
        />

        <SpectrumPlot pkg={pkg} scene={plotScene} lineSolo={lineSolo} palette={palette} />

        <PlotLegend
          stacked={stacked}
          hasStates={hasStates}
          primaryState={primary.state}
          comparisonState={comparison?.state ?? null}
          hasComparison={comparison !== null}
          showReddened={reddenedFlux !== null}
          showFeatures={featureScene !== null}
        />
      </div>

      <div className="timeline-panel">
        {stacked ? (
          <EpochMixer
            epochs={epochs}
            gains={stackedEpochGains}
            soloIndex={stackedSoloIndex}
            onGainChange={handleStackedEpochGainChange}
            onSoloToggle={handleStackedSoloToggle}
            onClearSolo={handleStackedClearSolo}
            onReset={handleStackedReset}
          />
        ) : (
          <Timeline
            epochs={epochs}
            cursorMjd={cursorMjd}
            primaryIndex={primaryIndex}
            comparisonIndex={comparisonIndex}
            mode={mode}
            onMjdChange={setCursorMjd}
            palette={palette}
          />
        )}
      </div>

      <div className="mixer-panel panel">
        <AudioSection audioReady={sonifier !== null} audioEnabled={audioEnabled} onToggleAudio={handleToggleAudio} />
        <EngineSection
          sonificationMode={sonificationMode}
          onSonificationModeChange={handleSonificationModeChange}
          stackedView={stacked}
          featureCounts={
            featureScene
              ? {
                  known: featureScene.analysis.features.filter((f) => f.knownKey !== null).length,
                  discovery: featureScene.analysis.features.filter((f) => f.knownKey === null).length,
                }
              : null
          }
          masterGain={masterGain}
          onMasterGainChange={setMasterGain}
          layerGains={layerGains}
          onLayerGainChange={(layer, value) => setLayerGains((prev) => ({ ...prev, [layer]: value }))}
          featureLayerGains={featureLayerGains}
          onFeatureLayerGainChange={(layer, value) =>
            setFeatureLayerGains((prev) => ({ ...prev, [layer]: value }))
          }
        />
        {featureModeActive ? (
          <>
            <SweepSection
              audioEnabled={audioEnabled}
              onPlaySweep={handlePlaySweep}
              sweepLoop={sweepLoop}
              onToggleSweepLoop={() => setSweepLoop((prev) => !prev)}
              sweepRate={sweepRate}
              onSweepRateChange={setSweepRate}
            />
            <ExtractionSection settings={extractionSettings} onChange={setExtractionSettings} />
          </>
        ) : null}
        <SoloSection lineSolo={lineSolo} onChange={setLineSolo} sweepAvailable={featureModeActive} />
        {stacked ? (
          <StackedNote />
        ) : (
          <>
            <CompareSection
              showDifference={showDifference}
              onToggleDifference={() => setShowDifference((v) => !v)}
              hasComparison={comparison !== null}
            />
            <DustSection
              active={mode === "reddening"}
              av={reddeningAv}
              onAvChange={setReddeningAv}
              law={reddeningLaw}
              onLawChange={setReddeningLaw}
              templateState={templateState}
              onTemplateStateChange={setReddeningTemplateState}
              templateOptions={templateOptions}
              benchmarks={object.reddening_benchmarks}
            />
          </>
        )}
      </div>

      <NarrativePanel
        object={object}
        epoch={primary}
        mode={mode}
        reddeningAv={reddeningAv}
        reddeningTemplateState={mode === "reddening" ? templateState : null}
      />
    </div>
  );
}
